import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import { FieldOption, resolveOptionLabel } from "./optionLabel";

/**
 * 単一選択のセレクトボックス。
 *
 * 旧実装はネイティブ `<select>` を使っていたが、OS / ブラウザごとに見た目が
 * 統一できず、Popover 系の他フォーム部品（`ComboInput`）と並んだときに
 * 「振込先銀行口座の口座種別」「プロジェクトタグの並び順」だけ妙に浮いて
 * 見えていた。`ComboInput` と同じカスタム Popover に揃え、`<select>` を廃止。
 *
 * 主な差分:
 *  - 見た目: 白背景 Popover + 青ハイライト、ホバー時は背景色のみ反転
 *  - 自由入力は不可（読み取り専用のボタン風コントロール）
 *  - キーボード操作: ↑↓ で候補移動 / Enter で確定 / Esc で閉じる
 *  - API: 既存の `<Select value options onChange />` をそのまま維持。
 *    `onChange` には `{ target: { name, value } }` shape を渡すので、
 *    呼び出し側で `e.target.value` が拾える（旧 ChangeEvent と互換）。
 */
export interface SelectOption {
  value: string;
  name: string;
}

export interface SelectChangeEvent {
  target: {
    name: string;
    value: string;
  };
}

export interface SelectProps {
  label?: string;
  /** ラベル右脇の補助テキスト（必須 / 任意）。 */
  option?: FieldOption | string;
  /** 未選択時のプレースホルダ。`options` 内に `{ value: "" }` がある場合はそちらが優先表示される。 */
  placeholder?: string;
  /** width 100% にしない場合（横並び）用。既定 "100%"。 */
  width?: string;
  /**
   * コントロールの密度。
   * - `"medium"`（既定）: Input と同じ縦サイズのフォーム用
   * - `"small"`: テーブルセル内などの省スペース用。枠線なし（ホバー / 開時に表示）
   */
  size?: "medium" | "small";
  name?: string;
  /** 現在の値（`options[].value` のいずれか）。 */
  value?: string;
  defaultValue?: string;
  onChange?: (e: SelectChangeEvent) => void;
  options: SelectOption[];
  disabled?: boolean;
  className?: string;
}

interface ControlButtonStyleProps {
  $hasValue: boolean;
  $open: boolean;
  $disabled: boolean;
  $small: boolean;
}

const ControlButton = styled.button.attrs<ControlButtonStyleProps>(
  ({ $small }) => ({
    type: "button" as const,
    className: $small ? "bodytext-13" : "bodytext",
  }),
)<ControlButtonStyleProps>`
  /* inline-block のベースライン余白で親セルが間延びしないよう block にする */
  display: block;
  width: 100%;
  /* medium: Input フォームと縦サイズを揃えるため、padding と line-height を soremo_style の
   * input[type="text"] と同等にする。右は dropdown icon のぶんだけ広げる。
   * small: テーブルセル内向けの高密度サイズ。 */
  padding: ${({ $small }) =>
    $small ? "2px 22px 2px 6px" : "12px 32px 12px 12px"};
  /* bodytext-13 の line-height 200% はセル内では間延びするため抑える */
  ${({ $small }) => ($small ? "line-height: 150%;" : "")}
  background: ${({ $small }) => ($small ? "transparent" : "#fff")};
  /* small はテーブルセル内用のため自前の枠を持たない（選択中の表示は
   * セル側の :focus-within リングに任せる） */
  border: 1px solid
    ${({ $open, $small }) =>
      $small
        ? "transparent"
        : $open
          ? "var(--soremo-blue)"
          : "var(--soremo-border)"};
  border-radius: 4px;
  /* small はセル内に溶け込ませるため、値ありの文字色は親（行）の色を継承する
   * （ダーク背景の行では白文字になる） */
  color: ${({ $hasValue, $small }) =>
    $hasValue
      ? $small
        ? "inherit"
        : "#000"
      : "var(--soremo-placeholder, var(--soremo-light-gray))"};
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  outline: none;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:hover:not(:disabled) {
    border-color: ${({ $small }) =>
      $small ? "transparent" : "var(--soremo-blue)"};
  }
  &:focus-visible {
    border-color: ${({ $small }) =>
      $small ? "transparent" : "var(--soremo-blue)"};
    box-shadow: ${({ $small }) =>
      $small ? "none" : "2px 4px 8px 3px rgba(0, 154, 206, 0.1)"};
  }

  &:disabled {
    opacity: 0.6;
    background: ${({ $small }) =>
      $small ? "transparent" : "var(--soremo-background, #fafafa)"};
  }
`;

const DropdownIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $open: boolean; $small: boolean }>`
  position: absolute;
  right: ${({ $small }) => ($small ? "2px" : "8px")};
  top: 50%;
  transform: translateY(-50%);
  font-size: ${({ $small }) => ($small ? "18px" : "24px")};
  color: ${({ $open }) =>
    $open ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
  pointer-events: none;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * 候補リスト。`document.body` へポータル描画するため fixed 配置。
 * テーブルの横スクロールコンテナ（overflow-x: auto）内など、祖先のクリップ
 * 境界に隠れない。位置はコントロールの getBoundingClientRect から算出する。
 */
const Popover = styled.ul<{ $small: boolean }>`
  position: fixed;
  /* small はコントロールが細いので、候補ラベルが折り返さない幅まで広げる */
  min-width: ${({ $small }) => ($small ? "max-content" : "0")};
  /* body へポータルするため、Modal（overlay 1000 / content 1001）より上に置く */
  z-index: 1100;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  max-height: 240px;
  overflow-y: auto;
`;

const PopoverItem = styled.li.attrs({ className: "bodytext-13" })<{
  $active: boolean;
  $selected: boolean;
}>`
  padding: 8px 12px;
  cursor: pointer;
  /* Popover は常に白背景のため、文字色は親から継承せず明示する
   * （テーブルの完了行など白文字コンテキスト内でも読めるように） */
  color: ${({ $selected }) => ($selected ? "var(--soremo-blue)" : "#000")};
  background-color: ${({ $active }) =>
    $active ? "var(--soremo-background, #f5f5f5)" : "transparent"};

  &:hover {
    background-color: var(--soremo-background, #f5f5f5);
  }
`;

const Select: React.FC<SelectProps> = ({
  label = "",
  option = "",
  placeholder,
  width = "100%",
  size = "medium",
  name,
  value,
  defaultValue,
  onChange,
  options,
  disabled = false,
  className,
}) => {
  const isSmall = size === "small";
  const currentValue = value !== undefined ? value : (defaultValue ?? "");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [popoverPos, setPopoverPos] = useState<React.CSSProperties>({});

  /** fixed 配置の候補リスト位置をコントロールの矩形から算出する。
   *  下に収まらない場合は上方向に開く。 */
  const updatePopoverPosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 240 && rect.top > spaceBelow;
    setPopoverPos({
      left: rect.left,
      width: rect.width,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  // 開いている間はスクロール・リサイズに追従して位置を更新する
  // （ポータル描画のため、祖先スクロールでは自動追従しない）。
  useLayoutEffect(() => {
    if (!open) return;
    updatePopoverPosition();
    window.addEventListener("scroll", updatePopoverPosition, true);
    window.addEventListener("resize", updatePopoverPosition);
    return () => {
      window.removeEventListener("scroll", updatePopoverPosition, true);
      window.removeEventListener("resize", updatePopoverPosition);
    };
  }, [open, updatePopoverPosition]);

  const selectedIndex = useMemo(
    () => options.findIndex((o) => o.value === currentValue),
    [options, currentValue],
  );
  const selectedOption =
    selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const { text: optionLabel, className: optionClass } =
    resolveOptionLabel(option);
  const containerClass =
    width === "100%"
      ? "u-col u-w100 u-gap4 u-relative"
      : "u-col u-fill u-gap4 u-relative";

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // 候補リストは body へポータル描画しているため、両方の外側だけ閉じる
      if (wrapperRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const openPopover = () => {
    if (disabled) return;
    setOpen(true);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  };

  const closePopover = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleSelect = useCallback(
    (opt: SelectOption) => {
      const synthetic: SelectChangeEvent = {
        target: { name: name ?? "", value: opt.value },
      };
      onChange?.(synthetic);
      closePopover();
      buttonRef.current?.focus();
    },
    [name, onChange],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        openPopover();
        return;
      }
      setActiveIndex((i) =>
        options.length === 0 ? -1 : (i + 1 + options.length) % options.length,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        openPopover();
        return;
      }
      setActiveIndex((i) =>
        options.length === 0 ? -1 : (i - 1 + options.length) % options.length,
      );
    } else if (e.key === "Enter" || e.key === " ") {
      if (!open) {
        e.preventDefault();
        openPopover();
        return;
      }
      if (activeIndex >= 0 && activeIndex < options.length) {
        e.preventDefault();
        handleSelect(options[activeIndex]);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        closePopover();
      }
    } else if (e.key === "Tab") {
      if (open) closePopover();
    }
  };

  const displayText =
    selectedOption?.name ??
    placeholder ??
    options.find((o) => o.value === "")?.name ??
    "選択してください";
  const hasValue = !!selectedOption && selectedOption.value !== "";

  return (
    <div className={`${containerClass} ${className ?? ""}`}>
      {label && (
        <label className="bodytext-13 u-line-height-100 u-pl4">
          {label}
          {optionLabel && <span className={optionClass}>{optionLabel}</span>}
        </label>
      )}
      <InputWrapper ref={wrapperRef}>
        <ControlButton
          ref={buttonRef}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={name ? `${name}-listbox` : undefined}
          aria-disabled={disabled || undefined}
          disabled={disabled}
          $hasValue={hasValue}
          $open={open}
          $disabled={disabled}
          $small={isSmall}
          onClick={() => (open ? closePopover() : openPopover())}
          onKeyDown={handleKeyDown}
        >
          {displayText}
        </ControlButton>
        <DropdownIcon $open={open} $small={isSmall}>
          arrow_drop_down
        </DropdownIcon>
        {open &&
          createPortal(
            <Popover
              ref={popoverRef}
              role="listbox"
              id={name ? `${name}-listbox` : undefined}
              $small={isSmall}
              style={popoverPos}
            >
              {options.length === 0 ? (
                <PopoverItem $active={false} $selected={false}>
                  候補なし
                </PopoverItem>
              ) : (
                options.map((opt, idx) => (
                  <PopoverItem
                    key={`${opt.value}-${idx}`}
                    role="option"
                    aria-selected={idx === selectedIndex}
                    $active={idx === activeIndex}
                    $selected={idx === selectedIndex}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(opt);
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    {opt.name}
                  </PopoverItem>
                ))
              )}
            </Popover>,
            document.body,
          )}
      </InputWrapper>
    </div>
  );
};

export default Select;
